/** Menu → Deals → Combos: the list of deals (F-DEAL-N1). */
import { useState } from 'react'
import { Edit, Package, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useOnlineOrderingEnabled } from '@/hooks/useOnlineOrderingEnabled'
import type { ComboView } from '@/types/deals'
import { ComboEditorDialog } from './ComboEditorDialog'
import { useCombos } from './DealsControls'
import { describeComboIncludes } from './dealsText'

export function CombosPane() {
  const { formatCurrency } = useCurrency()
  const online = useOnlineOrderingEnabled()
  const combosQuery = useCombos()
  const combos = combosQuery.data ?? []
  const [editing, setEditing] = useState<ComboView | null>(null)
  const [open, setOpen] = useState(false)

  const openEditor = (c: ComboView | null) => {
    setEditing(c)
    setOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl space-y-1">
          <h3 className="font-semibold">Combos</h3>
          <p className="text-sm text-muted-foreground">
            Fixed-price meals such as “Zinger Burger + side + drink”. Each deal is one line on the bill; the kitchen ticket lists what is inside.
          </p>
        </div>
        <Button type="button" className="gap-2" onClick={() => openEditor(null)}>
          <Plus className="h-4 w-4" />
          New deal
        </Button>
      </div>

      {combosQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : combosQuery.isError ? (
        <p className="text-sm text-destructive">Could not load deals. Refresh the page to try again.</p>
      ) : combos.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Package className="mx-auto mb-3 h-8 w-8 text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">
              No deals yet. Create one to sell a burger, side and drink together at one price.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="divide-y rounded-lg border">
          {combos.map((c) => {
            const saves = c.separate_price - c.price
            return (
              <div key={c.product_id} className="flex flex-wrap items-center gap-4 p-4">
                {c.image_url ? (
                  <img src={c.image_url} alt="" className="h-14 w-14 shrink-0 rounded-lg border object-cover" />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border bg-muted/40 text-muted-foreground">
                    <Package className="h-5 w-5" aria-hidden />
                  </div>
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{c.name}</span>
                    <span className="text-sm font-semibold tabular-nums">{formatCurrency(c.price)}</span>
                    {c.category_name && (
                      <Badge variant="outline" className="font-normal">
                        {c.category_name}
                      </Badge>
                    )}
                    {!c.is_available && <Badge variant="warning-surface">Not available</Badge>}
                    {online.enabled && !c.is_storefront_visible && <Badge variant="neutral-surface">Hidden online</Badge>}
                  </div>
                  <p className="truncate text-sm text-muted-foreground">
                    Includes: {describeComboIncludes(c.components, c.slots) || 'nothing yet'}
                  </p>
                  {c.separate_price > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Bought separately {formatCurrency(c.separate_price)}
                      {saves > 0 ? (
                        <> · saves {formatCurrency(saves)}</>
                      ) : saves < 0 ? (
                        <span className="text-amber-700 dark:text-amber-400"> · costs {formatCurrency(-saves)} more than buying separately</span>
                      ) : null}
                    </p>
                  )}
                </div>
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => openEditor(c)}>
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </div>
            )
          })}
        </div>
      )}

      <ComboEditorDialog open={open} onOpenChange={setOpen} combo={editing} />
    </div>
  )
}
